import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { Nothing } from 'components/Nothing';
import { type FC, Fragment, useState } from 'react';
import { getAuthorizationHeader } from 'redux/Api/employedsApi/employedsApi';
import { FormatAmount } from 'ui/FormatAmount';
import { capitalizeFirstLetter } from 'utils/strActions';
import download, { ReactComponent as IconDownload } from './img/iconDownload.svg';
import styles from './Reports.module.scss';
import { getMonthFromDate } from 'utils/date';
import { Button } from 'ui/Button';
import { Box, Divider, Group, Loader, Menu, Stack, Text } from '@mantine/core';
import { ReactComponent as KebabMenu } from './img/iconKebab.svg';
import { useGetReportsQuery } from 'redux/Api/profileApi/ProfileApi';
import { useMediaQuery } from '@mantine/hooks';

interface ReportsProps {
  openNav: boolean | undefined;
}

export const Reports: FC<ReportsProps> = ({ openNav }) => {
  const { data, isLoading } = useGetReportsQuery({});
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const isMobile = useMediaQuery(`(width < 768px)`);
  const isMediumScreen = useMediaQuery('(width >= 768px)');
  const isLessThan1595px = useMediaQuery('(width < 1595px)');
  const isLessThan1390px = useMediaQuery('(width < 1400px)');

  if (isLoading) {
    return (
      <div className={styles.progressBlock}>
        <LoadingSkeleton fullWidth={true} />
      </div>
    );
  }

  const getOneTimeToken = async (): Promise<{ token: string }> => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/customer/docs/download/one-time-token`,
      {
        method: 'GET',
        headers: {
          Authorization: getAuthorizationHeader(),
        },
      },
    );
    const token = await response.json();
    return token.token;
  };

  const handleDownload = async (file: string, prefix: string) => {
    try {
      setLoadingKey(file);

      const cloudLinkResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/customer/docs/${prefix}/${file}/cloud-download-link`,
        {
          method: 'GET',
          headers: {
            Authorization: getAuthorizationHeader(),
          },
        },
      );

      if (cloudLinkResponse.ok) {
        const cloudData = await cloudLinkResponse.json();
        const link = document.createElement('a');
        link.href = cloudData.href;
        link.target = '_self';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (cloudLinkResponse.status === 404) {
        const token = await getOneTimeToken();
        const url = `${process.env.REACT_APP_API_URL}/api/customer/docs/${prefix}/${file}/download/${token}`;
        window.open(url, '_self');
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoadingKey(null);
    }
  };

  const MobileMenu = ({ item, style }: { item: any; style: string }) => {
    return (
      <Menu shadow='md' width={240} position='bottom-end' classNames={styles.kebab}>
        <Menu.Target>
          <KebabMenu className={styles[style]} />
        </Menu.Target>

        <Menu.Dropdown>
          {item.order_report_file && (
            <Menu.Item
              p={'8px 16px'}
              style={{
                height: '48px',
              }}
              onClick={() => handleDownload(item.order_report_file, 'order')}
              leftSection={
                loadingKey === item.order_report_file ? (
                  <Loader size='xs' color='#002CFB' />
                ) : (
                  <IconDownload style={{ width: '18px', height: '18px' }} />
                )
              }>
              <div style={{ fontSize: '16px' }}> Отчет по заказам</div>
            </Menu.Item>
          )}
          {item.upd_file && (
            <Menu.Item
              p={'8px 16px'}
              onClick={() => handleDownload(item.upd_file, 'upd')}
              style={{
                height: '48px',
              }}
              leftSection={
                loadingKey === item.upd_file ? (
                  <Loader size='xs' color='#002CFB' />
                ) : (
                  <IconDownload style={{ width: '18px', height: '18px' }} />
                )
              }>
              <div style={{ fontSize: '16px' }}> УПД от платформы</div>
            </Menu.Item>
          )}
          {item.documents_file && (
            <Menu.Item
              p={'8px 16px'}
              onClick={() => handleDownload(item.documents_file, 'documents')}
              style={{
                height: '48px',
              }}
              leftSection={
                loadingKey === item.documents_file ? (
                  <Loader size='xs' color='#002CFB' />
                ) : (
                  <IconDownload style={{ width: '18px', height: '18px' }} />
                )
              }>
              <div style={{ fontSize: '16px' }}> Выгрузка 1С</div>
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    );
  };

  return (
    <div>
      {isMobile ? (
        <Box>
          {data?.data?.length ? (
            data?.data?.map((item: any) => (
              <Fragment key={item.time}>
                <Stack gap='4px' mb='8px' w='100%' pt='10px'>
                  <Group justify='space-between' mb='4px'>
                    <Text fz='16px' fw='500'>
                      {capitalizeFirstLetter(getMonthFromDate(item.time))}
                    </Text>
                    <MobileMenu item={item} style={'menu'} />
                  </Group>
                  <Group justify='space-between'>
                    <Text fz='14px' fw='400' c='#71869D'>
                      Завершено заказов
                    </Text>
                    <Text fz='16' c='#121922'>
                      {item.order_report_amount !== null ? item.order_report_amount : '-'}
                    </Text>
                  </Group>
                  <Group justify='space-between'>
                    <Text fz='14px' fw='400' c='#71869D'>
                      Услуги платформы
                    </Text>
                    <Text fz='16' c='#121922'>
                      {item.upd_amount !== null ? item.upd_amount : '-'}
                    </Text>
                  </Group>
                </Stack>
                <Divider mb='8px' w='100%' color='#EAF0FA' />
              </Fragment>
            ))
          ) : (
            <Nothing style={{ height: '200px' }} title='Ничего не найдено' />
          )}
        </Box>
      ) : null}

      {isMediumScreen ? (
        <Box>
          <div className={styles.table}>
            <div className={styles.table_title}>
              <p>Период</p>
              <p style={{ textAlign: 'end' }}>Завершено заказов</p>
              <p style={{ textAlign: 'end' }}>Услуги платформы</p>
            </div>
            {data?.data?.length ? (
              data?.data?.map((item: any) => (
                <div className={styles.table_row} key={item.time}>
                  <p>{capitalizeFirstLetter(getMonthFromDate(item.time))}</p>
                  <p style={{ textAlign: 'end' }}>
                    {item.order_report_amount !== null ? item.order_report_amount : '-'}
                  </p>
                  <FormatAmount amount={item.upd_amount} />
                  {!((!openNav && isLessThan1595px) || isLessThan1390px) ? (
                    <div className={styles.table_row_action}>
                      {item.order_report_file && (
                        <Button
                          icon
                          src={download}
                          theme='link'
                          loading={loadingKey === item.order_report_file}
                          onClick={() => handleDownload(item.order_report_file, 'order')}
                          className={styles.table_row_action_btn}>
                          Отчет по заказам
                        </Button>
                      )}
                      {item.upd_file && (
                        <Button
                          icon
                          src={download}
                          theme='link'
                          loading={loadingKey === item.upd_file}
                          onClick={() => handleDownload(item.upd_file, 'upd')}
                          className={styles.table_row_action_btn}>
                          УПД от платформы
                        </Button>
                      )}
                      {item.documents_file && (
                        <Button
                          icon
                          src={download}
                          theme='link'
                          loading={loadingKey === item.documents_file}
                          onClick={() => handleDownload(item.documents_file, 'documents')}
                          className={styles.table_row_action_btn}>
                          Выгрузка 1С
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'end',
                        alignItems: 'center',
                        paddingRight: '16px',
                      }}>
                      <MobileMenu item={item} style={'menu_tablet'} />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <Nothing style={{ height: '200px' }} title='Ничего не найдено' />
            )}
          </div>
        </Box>
      ) : null}
    </div>
  );
};
